/**
 * Sentinel list query factory and store-backed reading hook.
 *
 * Notes for callers:
 *  (a) `associatedObjects` is flat camelCase here, intentionally differing from
 *      `useCrmObjectList`'s nested snake_case `associated_objects`.
 *  (b) `previous` is dropped (matches CrmObjectListPage).
 *  (c) Refetch-on-missing is stricter than `useCrmObjectList`'s silent skip,
 *      satisfying AC#3.
 */

import { queryOptions } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';

import { logger } from '@/lib/logger';
import type {
  CrmObject,
  CrmObjectFilters,
  CrmObjectsListResponse,
} from '@/types/crm';

import type { EntityKindRef } from '../core/CRMObjectClient';
import { createCRMObjectClient } from '../core/CRMObjectClient';
import type { EntityTypeName } from '../core/types';
import type { CrmObjectListOptions } from '../query/queries';
import { crmQueryKeys } from '../query/queries';
import {
  getStoreGeneration,
  resolveEntityId,
  upsertSummaryObjects,
} from './crmObjectStore';
import { useStoreObjectsMap } from './hooks';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CrmObjectListSentinelPage {
  ids: string[];
  next: string | null;
  count: number | null;
  associated_objects?: Record<string, CrmObject>;
}

// Stable empty constant — avoids inline `[]` in useMemo dep comparisons.
const EMPTY_IDS: string[] = [];

// ---------------------------------------------------------------------------
// upsertListResponseToStore
// ---------------------------------------------------------------------------

/**
 * Upsert a CRM list response into the store and return a sentinel page.
 *
 * If `expectedGeneration` is provided and the store generation has changed
 * since the generation was snapshotted (before the network await), the upsert
 * is skipped — the data belongs to a different org session. The sentinel page
 * is always returned regardless so the React Query cache is still populated
 * with the id list and pagination metadata.
 */
export function upsertListResponseToStore(
  response: CrmObjectsListResponse,
  expectedGeneration?: number
): CrmObjectListSentinelPage {
  if (
    expectedGeneration === undefined ||
    getStoreGeneration() === expectedGeneration
  ) {
    upsertSummaryObjects(response.results);
    if (response.associated_objects) {
      upsertSummaryObjects(Object.values(response.associated_objects));
    }
  }

  return {
    ids: response.results.map(r => r.entity_id),
    next: response.next,
    count: response.count,
    associated_objects: response.associated_objects,
  };
}

// ---------------------------------------------------------------------------
// crmObjectListSentinelQuery
// ---------------------------------------------------------------------------

/**
 * Query options factory for listing CRM objects by entity type name.
 * Returns id-only sentinel pages; bodies are written to the CRM object store.
 */
export function crmObjectListSentinelQuery(
  teamId: number,
  entityType: EntityTypeName,
  filters?: Omit<CrmObjectFilters, 'entity_type_name'>,
  options?: CrmObjectListOptions
): ReturnType<typeof queryOptions<CrmObjectListSentinelPage>>;

/**
 * Query options factory for listing CRM objects by entity kind.
 * Returns id-only sentinel pages; bodies are written to the CRM object store.
 */
export function crmObjectListSentinelQuery(
  teamId: number,
  kindRef: EntityKindRef,
  filters?: Omit<CrmObjectFilters, 'entity_type_name' | 'entity_kind'>,
  options?: CrmObjectListOptions
): ReturnType<typeof queryOptions<CrmObjectListSentinelPage>>;

export function crmObjectListSentinelQuery(
  teamId: number,
  secondArg: EntityTypeName | EntityKindRef,
  filters?: Omit<CrmObjectFilters, 'entity_type_name' | 'entity_kind'>,
  options?: CrmObjectListOptions
): ReturnType<typeof queryOptions<CrmObjectListSentinelPage>> {
  if (typeof secondArg === 'string') {
    const entityType = secondArg;
    const client = createCRMObjectClient(teamId, entityType);

    return queryOptions<CrmObjectListSentinelPage>({
      queryKey: crmQueryKeys.objectsListUnified(
        teamId,
        entityType,
        'paged',
        'ids',
        {
          filters: filters as CrmObjectFilters | undefined,
          include_associated_objects:
            options?.include_associated_objects ?? false,
          include_associations: options?.include_associations ?? null,
        }
      ),
      queryFn: async () => {
        const gen = getStoreGeneration();
        const response = await client.list(filters, options);
        return upsertListResponseToStore(response, gen);
      },
      enabled: (options?.enabled ?? true) && !!teamId && teamId > 0,
    });
  } else {
    const { entity_kind } = secondArg;
    const client = createCRMObjectClient(teamId, { entity_kind });

    return queryOptions<CrmObjectListSentinelPage>({
      queryKey: [
        ...crmQueryKeys.objectsListByKind(teamId, entity_kind, filters),
        options?.include_associated_objects ?? false,
        JSON.stringify(options?.include_associations ?? null),
        'ids',
      ],
      queryFn: async () => {
        const gen = getStoreGeneration();
        const response = await client.list(filters, options);
        return upsertListResponseToStore(response, gen);
      },
      enabled: (options?.enabled ?? true) && !!teamId && teamId > 0,
    });
  }
}

// ---------------------------------------------------------------------------
// useStoreBackedList
// ---------------------------------------------------------------------------

/**
 * Return type of crmObjectListSentinelQuery passed to useQuery, shaped for
 * useStoreBackedList's input.
 */
interface SentinelQueryResult {
  data?: CrmObjectListSentinelPage;
  isLoading: boolean;
  isFetching: boolean;
  isPlaceholderData: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
}

export interface UseStoreBackedListResult {
  results: CrmObject[];
  associatedObjects: Record<string, CrmObject>;
  count: number | null;
  next: string | null;
  isLoading: boolean;
  isFetching: boolean;
  isPlaceholderData: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
}

/**
 * Read store-backed results from a sentinel list query.
 *
 * `query` should be the result of `useQuery(crmObjectListSentinelQuery(...))`.
 * This hook resolves the id list from the sentinel page into full CrmObjects
 * from the store, applying merge-alias and soft-delete guards. When the store
 * is missing bodies for returned ids it refetches (bounded to 3 attempts per
 * unique missing set + generation pair) to recover from stale cache.
 */
export function useStoreBackedList(
  query: SentinelQueryResult
): UseStoreBackedListResult {
  // ---- All hooks first (before any early returns) ----

  const ids = useMemo(() => query.data?.ids ?? EMPTY_IDS, [query.data?.ids]);

  const storeMap = useStoreObjectsMap(ids);

  // Classify results and missing ids in a single pass — order-preserving.
  const { results, missingIds } = useMemo(() => {
    const resolvedResults: CrmObject[] = [];
    const resolvedMissingIds: string[] = [];

    for (const id of ids) {
      // Merged-away: the alias map redirects this id to a different canonical
      // id. The survivor already appears under its own slot — drop the source.
      if (resolveEntityId(id) !== id) {
        continue;
      }
      const obj = storeMap.get(id);
      if (!obj) {
        // Missing from store — track for potential refetch.
        resolvedMissingIds.push(id);
      } else if (obj.deleted_at) {
        // Soft-deleted — intentional drop, not missing.
        continue;
      } else {
        resolvedResults.push(obj);
      }
    }

    return { results: resolvedResults, missingIds: resolvedMissingIds };
  }, [ids, storeMap]);

  // Associated objects — prefer store-fresh data when available and not deleted.
  const assocIds = useMemo(() => {
    const v = Object.values(query.data?.associated_objects ?? {}).map(
      o => o.entity_id
    );
    return v.length ? v : EMPTY_IDS;
  }, [query.data?.associated_objects]);

  const assocMap = useStoreObjectsMap(assocIds);

  const associatedObjects = useMemo((): Record<string, CrmObject> => {
    const base = query.data?.associated_objects ?? {};
    const result: Record<string, CrmObject> = {};
    for (const [key, obj] of Object.entries(base)) {
      const fresh = assocMap.get(obj.entity_id);
      result[key] = fresh && !fresh.deleted_at ? fresh : obj;
    }
    return result;
  }, [query.data?.associated_objects, assocMap]);

  // Refetch-on-missing — bounded, loop-safe (AC#3).
  // key = sorted missing ids + current generation, so:
  //   - a new generation (org switch / clearAll) re-arms the counter
  //   - a different set of missing ids also re-arms
  //   - the same set on the same generation is bounded to 3 attempts
  const attemptedRef = useRef<Set<string>>(new Set());
  const attemptRef = useRef(0);

  useEffect(() => {
    if (missingIds.length > 0) {
      if (!query.data || query.isFetching || query.isPlaceholderData) {
        return;
      }
      const key = [...missingIds].sort().join(',') + ':' + getStoreGeneration();
      if (attemptRef.current < 3 && !attemptedRef.current.has(key)) {
        attemptedRef.current.add(key);
        attemptRef.current += 1;
        logger.warn('sentinel list: store missing bodies for returned ids', {
          missingCount: missingIds.length,
          missingIds,
          generation: getStoreGeneration(),
        });
        void query.refetch();
      }
    } else {
      // All ids resolved — reset counters so a future missing set starts fresh.
      attemptRef.current = 0;
      attemptedRef.current.clear();
    }
  }, [missingIds, query]);

  // isLoading is true when:
  //  - the query itself is loading, OR
  //  - the query has data but some ids are still missing (and we haven't
  //    exhausted our refetch budget) — the consumer should show a loading state
  const isLoading =
    query.isLoading ||
    (!!query.data && missingIds.length > 0 && attemptRef.current < 3);

  return {
    results,
    associatedObjects,
    count: query.data?.count ?? null,
    next: query.data?.next ?? null,
    isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error,
    refetch: query.refetch,
  };
}
